// inactivityWatcher.js
// Crea un watcher de inactividad que usa BroadcastChannel cuando está disponible.
// No usa localStorage ni sessionStorage.

export function createInactivityWatcher(opts = {}) {
  const {
    inactivityLimitMs = 3 * 60 * 60 * 1000,
    inactivityWarningBeforeMs = 5 * 60 * 1000,
    onWarning = () => {},
    onExpired = () => {},
    onActivity = () => {}
  } = opts;

  let lastActivityAt = Date.now();
  let activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
  let broadcast = null;
  let warningTimer = null;
  let expireTimer = null;

  function _schedule() {
    clearTimers();
    const now = Date.now();
    const limitAt = (lastActivityAt || now) + inactivityLimitMs;
    const warningAt = limitAt - inactivityWarningBeforeMs;
    const msToWarning = Math.max(0, warningAt - now);
    const msToExpire = Math.max(0, limitAt - now);

    warningTimer = setTimeout(() => {
      try { onWarning({ remainingMs: Math.max(0, limitAt - Date.now()) }); } catch (e) {}
    }, msToWarning);

    expireTimer = setTimeout(() => {
      try { onExpired(); } catch (e) {}
    }, msToExpire);
  }

  function clearTimers() {
    if (warningTimer) { clearTimeout(warningTimer); warningTimer = null; }
    if (expireTimer) { clearTimeout(expireTimer); expireTimer = null; }
  }

  function _onActivityLocal() {
    lastActivityAt = Date.now();
    try { onActivity(); } catch (e) {}
    _schedule();
    // broadcast to other tabs if channel available
    try { if (broadcast) broadcast.postMessage({ type: 'activity', ts: lastActivityAt }); } catch (e) {}
  }

  function _onBroadcast(ev) {
    if (!ev || !ev.data) return;
    if (ev.data.type === 'activity') {
      const ts = Number(ev.data.ts) || Date.now();
      if (ts > lastActivityAt) {
        lastActivityAt = ts;
        try { onActivity(); } catch (e) {}
        _schedule();
      }
    }
  }

  function start() {
    // init broadcast channel if available
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        broadcast = new BroadcastChannel('ticketagrovin_inactivity');
        broadcast.addEventListener('message', _onBroadcast);
      }
    } catch (e) {
      broadcast = null;
    }

    activityEvents.forEach(evt => window.addEventListener(evt, _onActivityLocal, { passive: true }));
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') _onActivityLocal(); });
    lastActivityAt = Date.now();
    _schedule();
  }

  function stop() {
    activityEvents.forEach(evt => window.removeEventListener(evt, _onActivityLocal));
    try { document.removeEventListener('visibilitychange', _onActivityLocal); } catch (e) {}
    try { if (broadcast) { broadcast.removeEventListener('message', _onBroadcast); broadcast.close(); } } catch (e) {}
    broadcast = null;
    clearTimers();
  }

  function keepAlive() {
    lastActivityAt = Date.now();
    try { onActivity(); } catch (e) {}
    _schedule();
    try { if (broadcast) broadcast.postMessage({ type: 'activity', ts: lastActivityAt }); } catch (e) {}
  }

  return {
    start,
    stop,
    keepAlive,
    _internal: {
      getLastActivity: () => lastActivityAt
    }
  };
}
